import React, { FC, useContext, useEffect, useState } from 'react';

import { REPORT_SECTION, PeriodType } from '../../../../core/constants';
import { ROW_KIND } from '../../../../forecast/detail/reports/data-grid/constants';
import { DataGridContext, IDataGridContext } from '../../../../forecast/detail/reports/data-grid/context/data-grid';
import { PredictManagementContext } from '../../../../predict-management/context/predict-management-context';
import { NumberExplainedProps, NumberExplainedResponse } from './types';

import { NumberExplainedPopover } from './number-explained-popover';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';
import { useParams } from 'react-router-dom';
import { generateNumberExplainedLocalResponse } from './number-explained-messages';

export interface OwnProps {
  id: string;
  anchorEl: HTMLDivElement | null;
  open: boolean;
  onClose: () => void;
  actual?: number;
  predicted?: number;
}

export type Props = OwnProps & NumberExplainedProps['data'];

export const NumberExplained: FC<Props> = ({
  id,
  accountId,
  accountName,
  reportSection,
  isAiAssumption,
  type,
  anchorEl,
  open,
  onClose,
  actual,
  predicted
}) => {
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const { addErrorMessage } = useContext(PredictManagementContext);
  const { forecast, periodType } = usePredictionsInfoContext();
  const { tableRef } = useContext<Partial<IDataGridContext>>(DataGridContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<
    (NumberExplainedResponse & { actual?: number; predicted?: number; accountName?: string }) | null
  >(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (anchorEl !== null) {
        onClose();
      }
    };

    const tableEl = tableRef?.current;

    if (tableEl) {
      tableEl.addEventListener('scroll', handleScroll);
    }

    return () => {
      tableEl?.removeEventListener('scroll', handleScroll);
    };
  }, [tableRef, anchorEl, onClose]);

  useEffect(() => {
    const handleFetchNumberExplained = async (
      reportSection: REPORT_SECTION,
      accountId: string,
      type: ROW_KIND,
      periodType: PeriodType
    ) => {
      setLoading(true);

      try {
        let data;

        if (!workspaceId || !organisationId || !forecast?.id) {
          throw new Error('Missing params for fetching number explained.');
        } else if ([ROW_KIND.total, ROW_KIND.account].includes(type)) {
          data = generateNumberExplainedLocalResponse(reportSection, accountId, type, periodType);
        }

        setData({ ...(data as NumberExplainedResponse), actual, predicted, accountName });
        setError(null);
      } catch (error) {
        setData(null);
        setError(error as Error);
        addErrorMessage?.(new Error('Failed to get TNE'));
      } finally {
        setLoading(false);
      }
    };

    if (open && periodType) {
      handleFetchNumberExplained(reportSection, accountId, type, periodType);
    }
  }, [
    open,
    reportSection,
    accountId,
    type,
    id,
    periodType,
    workspaceId,
    organisationId,
    forecast,
    addErrorMessage,
    actual,
    predicted,
    accountName,
    isAiAssumption
  ]);

  if (!open) {
    return null;
  }

  if (isAiAssumption) {
    return null;
  }

  return <NumberExplainedPopover anchorEl={anchorEl} id={id} data={data} error={error} open={open} loading={loading} />;
};
